/**
 * Service to handle anonymous test results and link them to users
 */

import { apiClient } from './api/client';
import { User } from '../types/user';

interface AnonymousTestInfo {
  resultsToken: string;
  timestamp: number;
  primaryType?: number;
}

class AnonymousTestHandler {
  private STORAGE_KEY = 'anonymousTestResults';
  
  /**
   * Store anonymous test results, overwriting any previous results
   */
  storeTestResults(resultsToken: string, primaryType: number): void {
    // Store only the most recent test results
    const testInfo: AnonymousTestInfo = {
      resultsToken,
      timestamp: Date.now(),
      primaryType
    };
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(testInfo));
  }
  
  /**
   * Check if there are anonymous test results stored
   */
  hasStoredResults(): boolean {
    const testInfo = localStorage.getItem(this.STORAGE_KEY);
    return !!testInfo;
  }
  
  /**
   * Get stored anonymous test results
   */
  getStoredResults(): AnonymousTestInfo | null {
    const testInfo = localStorage.getItem(this.STORAGE_KEY);
    if (!testInfo) return null;
    
    try {
      return JSON.parse(testInfo) as AnonymousTestInfo;
    } catch (e) {
      console.error('Failed to parse stored test results:', e);
      return null;
    }
  }
  
  /**
   * Clear stored anonymous test results
   */
  clearStoredResults(): void {
    localStorage.removeItem(this.STORAGE_KEY);
  }
  
  /**
   * Associate anonymous test results with a user
   */
  async claimTestResults(userId: string): Promise<boolean> {
    const testInfo = this.getStoredResults();
    if (!testInfo) return false;
    
    try {
      // Call API to claim results
      await apiClient.post('/auth/claim-results', {
        userId,
        resultsToken: testInfo.resultsToken
      });
      
      // Clear stored results after successful claim
      this.clearStoredResults();
      return true;
    } catch (error) {
      console.error('Failed to claim test results:', error);
      return false;
    }
  }
}

export const anonymousTestHandler = new AnonymousTestHandler();
